import { Injectable } from '@angular/core';
import {
  Orders,
  OrderAdjustments,
  OrderItems,
  ItemAdjustments,
} from '../models/invoice/orders.model';
import { AuthorizationBase } from './authorize-base';
import {
  CartItem,
  OptionCalc,
  OptionsCalcTotals,
} from '../models/cart-item.model';

@Injectable({
  providedIn: 'root',
})
export class CartsOrdersCalculator extends AuthorizationBase {
  auth: AuthorizationBase;
  constructor() {
    super();
    this.auth = new AuthorizationBase();
  }

  public CartDataToOrders(cartItems: CartItem[]): Orders {
    let ordersModel = new Orders();
    let itemsOptionSubTotal = this.ItemsOptionSubTotal(cartItems);
    let itemsSubTotal = this.ItemsSubTotal(cartItems);
    let orderSubTotal = itemsSubTotal + itemsOptionSubTotal;
    let orderAdjustmentsSubTotal = this.GeneralAdjustMentSubTotal(
      cartItems,
      orderSubTotal
    );

    let loginuser = null;
    ordersModel.orderSubTotal = orderSubTotal;
    ordersModel.orderTotal = orderSubTotal - orderAdjustmentsSubTotal;
    ordersModel.terminalId = loginuser ? loginuser.TermId : 1;
    ordersModel.isEditable = true;
    ordersModel.orderItems = this.ProcessCartItems(cartItems);
    ordersModel.orderAdjustments = this.ProcessOrderAdjustments(
      cartItems,
      orderSubTotal
    );
    return ordersModel;
  }

  private ProcessCartItems(items: CartItem[]): OrderItems[] {
    let orderItems = items
      .filter((x) => x.Item.ProductName != 'GeneralAdjustment')
      .map((cartItem: CartItem) => {
        return {
          orderId: '',
          lineId: cartItem.Item.Id,
          itemName: cartItem.Item.ItemName,
          unitPrice: cartItem.Item.RecentPrice,
          quantity: cartItem.Qty,
          itemId: cartItem.Item.ItemId,
          itemVariationId: cartItem.ItemVariationId,
          itemVariationName: cartItem.Item.ItemVariantName,
          barcode: cartItem.Item.Barcode,
          lineTotal: cartItem.Price + cartItem.Item.ItemOptionsSubTotal,
          itemAdjustments: this.ProcessCartItemsAdjustments(cartItem),
        };
      });
    return orderItems;
  }

  public ProcessOrderAdjustments(items, tempSubtotal): OrderAdjustments[] {
    let orderAdjustments = items
      .filter((x) => x.Item.ProductName == 'GeneralAdjustment')
      .map((cartItem: CartItem) => {
        return {
          orderId: '',
          id: cartItem.Item.Id,
          adjustmentType: 'discount',
          adjustmentName: cartItem.Item.ItemName,
          amount: this.ProcessInlineDiscount(cartItem, tempSubtotal),
        };
      });
    return orderAdjustments;
  }

  private ProcessInlineDiscount(item: CartItem, subtotal: number) {
    let amount = 0;
    if (item.Item.DiscountPercent != null) {
      amount = (item.Item.DiscountPercent / 100) * subtotal;
    } else if (item.Item.DiscountAmount != null) {
      amount = item.Item.DiscountAmount;
    }
    return amount;
  }

  private ProcessCartItemsAdjustments(cartItem: CartItem): ItemAdjustments[] {
    if (!cartItem.Item.ItemOptionsCalc) {
      return [];
    }

    if (cartItem.Item.ProductName == 'GeneralAdjustment') {
      return [];
    }

    let itemAdjustments = cartItem.Item.ItemOptionsCalc.map((opt) => {
      return {
        orderId: '',
        lineId: cartItem.Item.Id,
        adjustmentType: opt.type,
        adjustmentName: opt.name,
        amount: this.adjustmentActualValue(opt, cartItem.Item.RecentPrice),
      };
    });
    return itemAdjustments;
  }

  private adjustmentActualValue(option: OptionCalc, itemPrice: number): number {
    let actualValue = option.isPercent
      ? itemPrice * (Number(option.value.replace('%', '')) / 100)
      : Number(option.value);
    return actualValue;
  }

  public ProcessOptionCalculation(
    cartItemOptions: OptionCalc[],
    price: number
  ) {
    let subTotal = 0;
    let optionsTotals: OptionsCalcTotals[] = [];

    if (cartItemOptions) {
      cartItemOptions.forEach((option) => {
        let actualValue = this.adjustmentActualValue(option, price);
        if (option.operation === 'minus') {
          subTotal -= actualValue;
          optionsTotals.push({
            type: option.type,
            totalValue: actualValue * -1,
          });
        } else {
          subTotal += actualValue;
          optionsTotals.push({ type: option.type, totalValue: actualValue });
        }
      });
    }

    return { subTotal: subTotal, optionsSummarized: optionsTotals };
  }

  public ProcessOptionsAggregation(cartItems: CartItem[]): any[] {
    let cartItemsOptionCalTotals = cartItems.map((c) => {
      return c.Item.ItemOptionsSummarized ? c.Item.ItemOptionsSummarized : [];
    });
    let listOfOptionsValues = [].concat(...cartItemsOptionCalTotals); // convert 2d array to 1d

    if (cartItemsOptionCalTotals.length === 0) {
      return [];
    }

    const result = Object.values(
      listOfOptionsValues.reduce(
        (r, o) => (
          r[o.type]
            ? (r[o.type].totalValue += o.totalValue)
            : (r[o.type] = { ...o }),
          r
        ),
        {}
      )
    );
    return result;
  }

  public ItemsSubTotal(cartItems: CartItem[]) {
    return cartItems
      .filter((x) => x.Item.ProductName != 'GeneralAdjustment')
      .map((c) => c.Price)
      .reduce((a, b) => a + b, 0);
  }

  public ItemsOptionSubTotal(cartItems: CartItem[]) {
    return cartItems
      .filter((x) => x.Item.ProductName != 'GeneralAdjustment')
      .map((c) => c.Item.ItemOptionsSubTotal)
      .reduce((a, b) => a + b, 0);
  }

  public GeneralAdjustMentSubTotal(
    cartItems: CartItem[],
    cartSubTotal: number
  ) {
    return cartItems
      .filter((x) => x.Item.ProductName == 'GeneralAdjustment')
      .map((c) => this.ProcessInlineDiscount(c, cartSubTotal))
      .reduce((a, b) => a + b, 0);
  }
}
